import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A vertical grouped lollipop chart displays multiple data series as vertical lines, each
          ending with a circular marker. The lines are grouped by category, allowing for easy
          comparison of subcategories within each group. This chart is ideal for comparing multiple
          variables across different categories while maintaining a clear and visually engaging
          design.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Vertical grouped lollipop charts are particularly effective for:</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Comparing Multiple Data Series:</strong> They effectively showcase multiple
            variables side by side within each category, such as comparing sales performance across
            products and regions.
          </li>
          <li>
            <strong>Highlighting Variations Across Categories:</strong> The grouped structure allows
            clear comparisons within categories, such as analyzing test scores across different
            subjects for multiple classes.
          </li>
          <li>
            <strong>Providing a Clean, Modern Visual Alternative:</strong> Vertical grouped lollipop
            charts present grouped data in a visually minimalistic way, reducing clutter compared to
            traditional grouped bar charts.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Vertical grouped lollipop charts are a useful tool for comparing multiple categories
          across different groups, but they come with certain limitations that should be considered:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Space for Group Labels:</strong> When there are many groups, the space
            available for labels can become cramped, making it challenging to read and interpret the
            data, especially when the labels are long or the groups are numerous.
          </li>
          <li>
            <strong>Difficulty in Comparing Multiple Groups:</strong> While grouped lollipop charts
            are effective for comparing categories within a single group, it can be hard to
            accurately compare data points across multiple groups due to overlapping lines or visual
            clutter.
          </li>
          <li>
            <strong>Challenges with Small Data Differences:</strong> When there are small
            differences between data points across groups, vertical grouped lollipop charts may
            struggle to represent these differences clearly, potentially leading to
            misinterpretations.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          For vertical grouped lollipop charts, color selection is key to differentiating data
          groups and ensuring clarity. Using distinct colors for each group makes comparisons
          easier, while gradients can highlight progression or magnitude. Thoughtful color choices
          maintain readability and prevent visual clutter, ensuring the chart remains clear and
          accessible.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Vertical grouped lollipop charts are effective for visualizing categorical comparisons,
          but they come with specific challenges that should be considered:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Cluttered Display:</strong> When too many groups are present, the chart can
            become crowded, making it difficult to distinguish individual data points and compare
            them effectively.
          </li>
          <li>
            <strong>Difficulty in Accurate Comparison:</strong> The lollipop markers, often closely
            spaced, can make it challenging to compare values accurately, especially when the
            differences are small.
          </li>
          <li>
            <strong>Limited Label Space:</strong> The vertical layout can limit the space available
            for labels, causing them to overlap or be truncated, which reduces readability.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
